import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Barbell, ArrowRight, Spinner, LockKey, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-token/${token}`)
      .then(() => setValid(true))
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-6 py-12">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <Link to="/login" className="flex items-center gap-2 mb-12">
          <Barbell size={28} weight="fill" color="#FF3B30" />
          <span className="font-head text-2xl font-extrabold uppercase tracking-tight">Athlete<span className="text-[#FF3B30]">Hub</span></span>
        </Link>

        {checking ? (
          <div className="flex items-center gap-3 text-[#737373]" data-testid="reset-checking"><Spinner size={20} className="animate-spin" /> Verifying link…</div>
        ) : done ? (
          <div data-testid="reset-success">
            <div className="w-14 h-14 rounded-sm bg-[#34C759]/15 flex items-center justify-center mb-6"><CheckCircle size={30} weight="fill" color="#34C759" /></div>
            <h1 className="font-head text-4xl font-black uppercase tracking-tighter leading-none mb-3">Password reset</h1>
            <p className="text-[#A3A3A3] mb-8">Your password has been updated. Redirecting you to sign in…</p>
            <Link to="/login" className="inline-flex items-center gap-2 text-[#FF3B30] font-bold uppercase tracking-widest text-sm hover:text-[#FF5C53] transition-colors">Sign in now <ArrowRight size={16} weight="bold" /></Link>
          </div>
        ) : !valid ? (
          <div data-testid="reset-invalid">
            <div className="w-14 h-14 rounded-sm bg-[#FF3B30]/15 flex items-center justify-center mb-6"><WarningCircle size={30} weight="fill" color="#FF3B30" /></div>
            <h1 className="font-head text-4xl font-black uppercase tracking-tighter leading-none mb-3">Link expired</h1>
            <p className="text-[#A3A3A3] mb-8">This reset link is invalid or has expired. Please request a new one.</p>
            <Link to="/forgot-password" data-testid="request-new-link" className="inline-flex items-center gap-2 text-[#FF3B30] font-bold uppercase tracking-widest text-sm hover:text-[#FF5C53] transition-colors">Request new link <ArrowRight size={16} weight="bold" /></Link>
          </div>
        ) : (
          <>
            <h1 className="font-head text-4xl md:text-5xl font-black uppercase tracking-tighter leading-none mb-2">New password</h1>
            <p className="text-[#A3A3A3] mb-10">Choose a strong password for your account.</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">New password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password-input"
                  className="w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-sm p-4 placeholder-gray-600 outline-none transition-colors" placeholder="••••••••" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">Confirm password</label>
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input"
                  className="w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-sm p-4 placeholder-gray-600 outline-none transition-colors" placeholder="••••••••" />
              </div>
              <button type="submit" disabled={loading} data-testid="reset-submit-button"
                className="w-full flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#FF5C53] disabled:opacity-60 text-white font-bold uppercase tracking-widest px-8 py-4 rounded-sm transition-colors">
                {loading ? <Spinner size={20} className="animate-spin" /> : <><LockKey size={18} weight="bold" /> Reset password</>}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
